/**
 * Role-based access control (RBAC) for LabCallManager.
 *
 * Roles (least → most privileged):
 *   Receptionist → Operatore → Supervisore → Admin → SuperAdmin
 */

export type AppRole = 'Receptionist' | 'Operatore' | 'Supervisore' | 'Admin' | 'SuperAdmin';

// ─── Role hierarchy ────────────────────────────────────────────────────────────
const ROLE_RANK: Record<AppRole, number> = {
  Receptionist: 0,
  Operatore: 1,
  Supervisore: 2,
  Admin: 3,
  SuperAdmin: 4,
};

/** Returns true if `userRole` is at least as privileged as `minRole`. */
export function hasMinRole(userRole: string | undefined, minRole: AppRole): boolean {
  const rank = ROLE_RANK[userRole as AppRole] ?? -1;
  return rank >= ROLE_RANK[minRole];
}

/** Returns true if `userRole` exactly matches one of the allowed roles. */
export function hasRole(userRole: string | undefined, ...allowed: AppRole[]): boolean {
  return allowed.includes(userRole as AppRole);
}

// ─── Page-level access map ─────────────────────────────────────────────────────
/**
 * Maps route prefixes to the minimum role required to access them.
 * Routes not listed here are accessible to all authenticated users.
 */
export const PAGE_MIN_ROLE: Record<string, AppRole> = {
  // SuperAdmin-only
  '/superadmin': 'SuperAdmin',

  // Admin+
  '/admin': 'Admin',
  '/audit-trail': 'Admin',
  '/feature-flags': 'Admin',
  '/lab/data-retention': 'Admin',
  '/lab/webhooks': 'Admin',
  '/lab/api': 'Admin',
  '/lab/roles': 'Admin',
  '/lab/lis': 'Admin',
  '/lab/integrations': 'Admin',
  '/lab/billing': 'Admin',
  '/lab/upgrade': 'Admin',
  '/numbers-management': 'Admin',
  '/twilio-health': 'Admin',
  '/verify-security': 'Admin',
  '/session-replay': 'Admin',
  '/billing-advanced': 'Admin',
  '/billing-dashboard': 'Admin',
  '/agreements': 'Admin',
  '/branches': 'Admin',
  '/studio-manager': 'Admin',
  '/onboarding': 'Admin',
  '/scripts': 'Admin',
  '/announcements': 'Admin',
  '/alert-notifications': 'Admin',
  '/goals/config': 'Admin',
  '/lab/sla': 'Admin',
  '/lab/profile': 'Admin',
  '/lab/team': 'Admin',
  '/lab/hours': 'Admin',
  '/lab/notifications': 'Admin',
  '/lab/sms-templates': 'Admin',
  '/lab/reminders': 'Admin',
  '/physicians': 'Admin',
  '/quality': 'Admin',
  '/export': 'Admin',

  // Supervisore+
  '/supervisor': 'Supervisore',
  '/wallboard': 'Supervisore',
  '/operator-leaderboard': 'Supervisore',
  '/operator-comparison': 'Supervisore',
  '/supervisor-broadcast': 'Supervisore',
  '/analytics': 'Supervisore',
  '/reports': 'Supervisore',
  '/operators': 'Supervisore',
  '/handover-history': 'Supervisore',
  '/operator-sla': 'Supervisore',
  '/ai-insights': 'Supervisore',
  '/results/ai-insights': 'Supervisore',
  '/voice-intelligence': 'Supervisore',

  // Operatore+ (Receptionist cannot access)
  '/call-routing': 'Operatore',
  '/browser-calling': 'Operatore',
  '/twilio-flex': 'Operatore',
  '/call-whisper': 'Operatore',
  '/call-recordings': 'Operatore',
  '/blacklist': 'Operatore',
  '/blacklist/rules': 'Operatore',
  '/campaigns': 'Operatore',
  '/sms': 'Operatore',
  '/whatsapp-notifications': 'Operatore',
  '/conversations': 'Operatore',
  '/unified-conversations': 'Operatore',
  '/video-consultation': 'Operatore',
  '/twilio-notify': 'Operatore',
  '/followup-automation': 'Operatore',
  '/exam-catalog': 'Operatore',
  '/exam-prep': 'Operatore',
  '/exams': 'Operatore',
  '/prescriptions': 'Operatore',
  '/shifts': 'Operatore',
  '/shift-swap': 'Operatore',
  '/worked-hours': 'Operatore',
  '/availability-grid': 'Operatore',
  '/weekly-availability': 'Operatore',
  '/daily-goals': 'Operatore',
  '/operator-chat': 'Operatore',
  '/data-import': 'Operatore',
};

/**
 * Returns the minimum role required for a given pathname.
 * Falls back to null (no restriction) if the path is not listed.
 */
export function getRequiredRole(pathname: string): AppRole | null {
  // Exact match first
  if (PAGE_MIN_ROLE[pathname]) return PAGE_MIN_ROLE[pathname];

  // Prefix match (longest prefix wins)
  let bestMatch: string | null = null;
  for (const prefix of Object.keys(PAGE_MIN_ROLE)) {
    if (pathname.startsWith(prefix + '/') || pathname === prefix) {
      if (!bestMatch || prefix.length > bestMatch.length) {
        bestMatch = prefix;
      }
    }
  }
  return bestMatch ? PAGE_MIN_ROLE[bestMatch] : null;
}

/** Returns true if the user can access the given pathname. */
export function canAccessPage(userRole: string | undefined, pathname: string): boolean {
  const required = getRequiredRole(pathname);
  if (!required) return true;
  return hasMinRole(userRole, required);
}

// ─── Action-level permissions ──────────────────────────────────────────────────

export const PERMISSIONS = {
  // Operator management
  canCreateOperator: (role: string) => hasMinRole(role, 'Admin'),
  canEditOperator: (role: string) => hasMinRole(role, 'Admin'),
  canDeleteOperator: (role: string) => hasMinRole(role, 'Admin'),
  canViewAllOperators: (role: string) => hasMinRole(role, 'Supervisore'),

  // Patient management
  canImportPatients: (role: string) => hasMinRole(role, 'Operatore'),
  canDeletePatient: (role: string) => hasMinRole(role, 'Admin'),
  canMergePatients: (role: string) => hasMinRole(role, 'Admin'),
  canExportPatients: (role: string) => hasMinRole(role, 'Admin'),
  canViewPatientPII: (role: string) => hasMinRole(role, 'Operatore'),

  // Calls
  canAssignCall: (role: string) => hasMinRole(role, 'Operatore'),
  canTransferCall: (role: string) => hasMinRole(role, 'Operatore'),
  canDeleteCall: (role: string) => hasMinRole(role, 'Admin'),
  canViewCallRecordings: (role: string) => hasMinRole(role, 'Operatore'),

  // Results / exams
  canSignResult: (role: string) => hasMinRole(role, 'Supervisore'),
  canSendResult: (role: string) => hasMinRole(role, 'Operatore'),
  canViewCriticalResults: (role: string) => hasMinRole(role, 'Operatore'),

  // Analytics
  canViewAnalytics: (role: string) => hasMinRole(role, 'Supervisore'),
  canViewAIInsights: (role: string) => hasMinRole(role, 'Supervisore'),

  // Lab settings
  canEditLabSettings: (role: string) => hasMinRole(role, 'Admin'),
  canManageIntegrations: (role: string) => hasMinRole(role, 'Admin'),
  canManageBilling: (role: string) => hasMinRole(role, 'Admin'),

  // Supervisor actions
  canBroadcast: (role: string) => hasMinRole(role, 'Supervisore'),
  canViewWallboard: (role: string) => hasMinRole(role, 'Supervisore'),
  canViewLeaderboard: (role: string) => hasMinRole(role, 'Supervisore'),

  // Admin actions
  canAccessAuditTrail: (role: string) => hasMinRole(role, 'Admin'),
  canManageFeatureFlags: (role: string) => hasMinRole(role, 'Admin'),
  canManageScripts: (role: string) => hasMinRole(role, 'Admin'),

  // SuperAdmin only
  canAccessSuperAdmin: (role: string) => hasRole(role, 'SuperAdmin'),
  canImpersonate: (role: string) => hasRole(role, 'SuperAdmin'),
  canManageTenants: (role: string) => hasRole(role, 'SuperAdmin'),
} as const;

// ─── Sidebar group visibility ──────────────────────────────────────────────────

export const ROLE_ALLOWED_GROUPS: Record<AppRole, string[]> = {
  Receptionist: ['core', 'patients', 'agenda'],
  Operatore: ['core', 'patients', 'lab-workflow', 'lab-catalog', 'agenda', 'communications', 'followups-group', 'calls-advanced'],
  Supervisore: ['core', 'patients', 'lab-workflow', 'lab-catalog', 'agenda', 'communications', 'followups-group', 'calls-advanced', 'operators', 'shifts-group', 'analytics'],
  Admin: ['core', 'patients', 'lab-workflow', 'lab-catalog', 'lab-admin', 'lab-config', 'lab-comms', 'agenda', 'communications', 'followups-group', 'calls-advanced', 'twilio-advanced', 'operators', 'shifts-group', 'analytics', 'config', 'admin'],
  SuperAdmin: ['core', 'patients', 'lab-workflow', 'lab-catalog', 'lab-admin', 'lab-config', 'lab-comms', 'agenda', 'communications', 'followups-group', 'calls-advanced', 'twilio-advanced', 'operators', 'shifts-group', 'analytics', 'config', 'admin'],
};

export const ROLE_HIDDEN_ITEMS: Record<AppRole, string[]> = {
  Receptionist: [
    'nav-admin', 'nav-audit-trail', 'nav-feature-flags', 'nav-superadmin',
    'nav-data-retention', 'nav-supervisor', 'nav-wallboard', 'nav-leaderboard',
    'nav-comparison', 'nav-session-replay', 'nav-call-recordings', 'nav-call-whisper',
    'nav-voice-intelligence', 'nav-browser-calling', 'nav-twilio-flex',
    'nav-blacklist', 'nav-campaigns', 'nav-sms', 'nav-whatsapp', 'nav-conversations',
    'nav-unified-conv', 'nav-video', 'nav-notify', 'nav-followup-auto',
    'nav-routing', 'nav-broadcast', 'nav-export', 'nav-analytics',
    'nav-analytics-telecom', 'nav-epidemiology', 'nav-ai-insights', 'nav-reports',
    'nav-invoices', 'nav-billing-dashboard', 'nav-billing-advanced',
    'nav-physicians', 'nav-agreements', 'nav-quality', 'nav-branches',
    'nav-lab-profile', 'nav-lab-team', 'nav-lab-roles', 'nav-lab-hours',
    'nav-reminders', 'nav-lis', 'nav-lab-integrations', 'nav-lab-webhooks',
    'nav-lab-api', 'nav-lab-sla', 'nav-lab-notifications', 'nav-sms-templates',
    'nav-scripts', 'nav-studio', 'nav-announcements', 'nav-numbers-mgmt',
    'nav-twilio-health', 'nav-alert-notifications', 'nav-verify-security',
    'nav-jobs', 'nav-onboarding', 'nav-shifts', 'nav-shift-swap',
    'nav-worked-hours', 'nav-avail-grid', 'nav-weekly-avail', 'nav-daily-goals',
    'nav-operator-sla', 'nav-handover-history', 'nav-session-replay',
    'nav-operator-dashboard', 'nav-operator-presence',
  ],
  Operatore: [
    'nav-admin', 'nav-audit-trail', 'nav-feature-flags', 'nav-superadmin',
    'nav-data-retention', 'nav-supervisor', 'nav-wallboard', 'nav-leaderboard',
    'nav-comparison', 'nav-session-replay',
  ],
  Supervisore: [
    'nav-admin', 'nav-audit-trail', 'nav-feature-flags', 'nav-superadmin',
    'nav-data-retention',
  ],
  Admin: ['nav-superadmin'],
  SuperAdmin: [],
};

export function getAllowedGroups(role: string | undefined): string[] {
  return ROLE_ALLOWED_GROUPS[(role as AppRole) ?? 'Receptionist'] ?? ROLE_ALLOWED_GROUPS['Receptionist'];
}

export function getHiddenItems(role: string | undefined): string[] {
  return ROLE_HIDDEN_ITEMS[(role as AppRole) ?? 'Receptionist'] ?? ROLE_HIDDEN_ITEMS['Receptionist'];
}
